/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 * Modified by TMC. 2023/08
 */

// 公式の Plugins Examples を移植して必要な箇所を変更
// https://github.com/facebook/lexical/blob/102b01d808f53b560ddbd3f7dcc53ba34d24e4e1/packages/lexical-playground/src/plugins/MentionsPlugin/index.tsx
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  MenuRenderFn,
  MenuTextMatch,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin'
import { TextNode } from 'lexical'
import { FC, RefObject, useCallback, useMemo, useState } from 'react'
import * as ReactDOM from 'react-dom'

import { MentionType } from '@crew/enums/app'
import { EntityType } from '@crew/enums/domain'
import { MentionItem, useMentionList } from '@crew/hooks'
import { generateImageAvatarUrl } from '@crew/utils/avatar'

import {
  CrewAvatar,
  CrewAvatarSize,
} from 'components/elements/crewAvatar/crewAvatar'
import { mention as mentionConst } from '@crew/configs/constants'
import { SPACE_CHARACTER } from 'configs/constants'
import { useAppSelector } from 'states/hooks'

import { $createMentionNode } from '../../nodes/mentionNode'

const PUNCTUATION =
  '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;'
const NAME = '\\b[A-Z][^\\s' + PUNCTUATION + ']'

const DocumentMentionsRegex = {
  NAME,
  PUNCTUATION,
}

const PUNC = DocumentMentionsRegex.PUNCTUATION

const TRIGGERS = ['@'].join('')

// Chars we expect to see in a mention (non-space, non-punctuation).
const VALID_CHARS = '[^' + TRIGGERS + PUNC + '\\s]'

// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
const VALID_JOINS =
  '(?:' +
  '\\.[ |$]|' + // E.g. "r. " in "Mr. Smith"
  ' |' + // E.g. " " in "Josh Duck"
  '[' +
  PUNC +
  ']|' + // E.g. "-' in "Salier-Hellendag"
  ')'

const LENGTH_LIMIT = 75

const AtSignMentionsRegex = new RegExp(
  '(^|\\s|\\()(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    VALID_JOINS +
    '){0,' +
    LENGTH_LIMIT +
    '})' +
    ')$'
)

// 50 is the longest alias length limit.
const ALIAS_LENGTH_LIMIT = 50

// Regex used to match alias.
const AtSignMentionsRegexAliasRegex = new RegExp(
  '(^|\\s|\\()(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    '){0,' +
    ALIAS_LENGTH_LIMIT +
    '})' +
    ')$'
)

/**
 * メンション入力かどうかのチェック
 * @param text 入力値
 * @param minMatchLength @以降、何文字入力でメンション入力とみなすか
 * @returns
 */
const checkForAtSignMentions = (
  text: string,
  minMatchLength: number
): MenuTextMatch | null => {
  let match = AtSignMentionsRegex.exec(text)

  if (match === null) {
    match = AtSignMentionsRegexAliasRegex.exec(text)
  }
  if (match !== null) {
    // The strategy ignores leading whitespace but we need to know it's
    // length to add it to the leadOffset
    const maybeLeadingWhitespace = match[1]

    const matchingString = match[3]
    if (matchingString.length >= minMatchLength) {
      return {
        leadOffset: match.index + maybeLeadingWhitespace.length,
        matchingString,
        replaceableString: match[2],
      }
    }
  }
  return null
}

/**
 * 入力値がメンションとしてみなすことができるかの情報を返す
 * @param text 入力値
 * @returns
 */
const getPossibleMenuTextMatch = (text: string): MenuTextMatch | null => {
  // 公式サンプルでは、@ + 1文字以上入力でメンションリストが表示される仕様だったところを
  // CrewWorksでは、@入力でメンションリストを表示するように変更した
  return checkForAtSignMentions(text, 0)
}

class MentionMenuOption extends MenuOption {
  name: string
  item: MentionItem

  constructor(name: string, item: MentionItem) {
    super(name)
    this.name = name
    this.item = item
  }
}

function MentionsTypeaheadMenuItem({
  index,
  isSelected,
  onClick,
  onMouseEnter,
  option,
}: {
  index: string
  isSelected: boolean
  onClick: () => void
  onMouseEnter: () => void
  option: MentionMenuOption
}) {
  let className =
    'item crew-bg-default text-crew-gray-4-light dark:text-crew-gray-1-dark'
  if (isSelected) {
    className += ' selected dark:text-crew-gray-4-light'
  }
  return (
    <li
      key={option.key}
      tabIndex={-1}
      className={className}
      ref={option.setRefElement}
      role="option"
      aria-selected={isSelected}
      id={'typeahead-item-' + index}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      <div className="flex items-center gap-x-2">
        {/* エディタには選択後のdisplayNameが反映されるので、 
            通知範囲指定メンションのリスト項目表示にlabelを使用する */}
        {option.item.id === MentionType.All.id ||
        option.item.id === MentionType.Here.id ? (
          <>
            <div className="font-bold">{option.item.id}</div>
            <span>{option.item.listItemLabel}</span>
          </>
        ) : (
          <>
            <CrewAvatar
              displayName={option.item.displayName}
              size={CrewAvatarSize.xs}
              imageURL={generateImageAvatarUrl(EntityType.User, option.item.id)}
              cacheValue={option.item.id + option.item.version}
            />
            <span className="text">{option.name}</span>
          </>
        )}
      </div>
    </li>
  )
}

type Props = {
  entityType?: EntityType
  entityRecordId?: string
  disabled: boolean
  editorContainerRef?: RefObject<HTMLDivElement>
}

/**
 * メンションプラグイン
 * @returns
 */
export const MentionsPlugin: FC<Props> = (props) => {
  const [editor] = useLexicalComposerContext()
  const [queryString, setQueryString] = useState<string | null>(null)
  const loggedInUser = useAppSelector((state) => state.app.loggedInUser)

  //TODO: queryString がキーボード入力内容そのまま入ってくるので、そのたびにバックエンドにリクエストが飛んでしまう。
  //      https://break-tmc.atlassian.net/browse/CREW-6747
  // メンションリスト用データ
  const { targets: mentionList } = useMentionList(
    props.entityType,
    props.entityRecordId,
    queryString
  )

  const checkForSlashTriggerMatch = useBasicTypeaheadTriggerMatch('/', {
    minLength: 0,
  })

  // メンションリスト
  const options = useMemo(() => {
    return mentionList.map(
      (mention) => new MentionMenuOption(mention.displayName, mention)
    )
  }, [mentionList])

  // メンションリストからItemを選択したとき
  const onSelectOption = useCallback(
    (
      selectedOption: MentionMenuOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void
    ) => {
      editor.update(() => {
        let mentionStyleClass: string =
          mentionConst.STYLE_MENTION_OTHER_CLASS_NAME
        //style mention by me class
        if (selectedOption.item.id === loggedInUser?.id) {
          mentionStyleClass = mentionConst.STYLE_MENTION_ME_CLASS_NAME
        } else if (
          selectedOption.item.id === MentionType.All.id ||
          selectedOption.item.id === MentionType.Here.id
        ) {
          //style mention all or here class
          mentionStyleClass = mentionConst.STYLE_MENTION_ALL_HERE_CLASS_NAME
        }
        const mentionNode = $createMentionNode(
          selectedOption.name,
          selectedOption.item.id,
          mentionStyleClass
        )
        if (nodeToReplace) {
          nodeToReplace.replace(mentionNode)
        }
        // メンションを確定させたときに半角スペースが自動的
        mentionNode.select().insertText(SPACE_CHARACTER)
        closeMenu()
      })
    },
    [editor, loggedInUser?.id]
  )

  // メンションリスト表示するタイミングを判定する
  const checkForMentionMatch = useCallback(
    (text: string) => {
      const mentionMatch = getPossibleMenuTextMatch(text)
      const slashMatch = checkForSlashTriggerMatch(text, editor)
      return !slashMatch && mentionMatch && !props.disabled
        ? mentionMatch
        : null
    },
    [checkForSlashTriggerMatch, editor, props.disabled]
  )

  //callback on typeahead menu close
  const handleMentionMenuItemClose = useCallback(() => {
    //enable editor container scroll when typeahead menu is closed
    if (props.editorContainerRef?.current) {
      props.editorContainerRef.current.style.overflow = 'auto'
    }
  }, [props.editorContainerRef])

  // Render mention list menu item
  const renderMentionMenuItem = useCallback<MenuRenderFn<MentionMenuOption>>(
    (
      anchorElementRef,
      { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }
    ) => {
      if (anchorElementRef.current && mentionList.length) {
        //disable editor container scroll when typeahead menu is open
        if (props.editorContainerRef?.current) {
          props.editorContainerRef.current.style.overflow = 'hidden'
        }

        return ReactDOM.createPortal(
          <div className="typeahead-popover mentions-menu crew-bg-default">
            <ul>
              {options.map((option, index: number) => (
                <MentionsTypeaheadMenuItem
                  index={option.key}
                  isSelected={selectedIndex === index}
                  onClick={() => {
                    setHighlightedIndex(index)
                    selectOptionAndCleanUp(option)
                  }}
                  onMouseEnter={() => {
                    setHighlightedIndex(index)
                  }}
                  key={option.key}
                  option={option}
                />
              ))}
            </ul>
          </div>,
          anchorElementRef.current as HTMLElement
        )
      }

      return null
    },
    [mentionList.length, options, props.editorContainerRef]
  )

  return (
    <LexicalTypeaheadMenuPlugin<MentionMenuOption>
      onQueryChange={setQueryString} // 入力値（@ 以降）
      onSelectOption={onSelectOption} // メンションリストのItem選択
      triggerFn={checkForMentionMatch} // メンションリスト表示タイミング
      options={options} // メンションリスト
      onClose={handleMentionMenuItemClose}
      menuRenderFn={renderMentionMenuItem}
    />
  )
}
